import React, { useEffect, useMemo, useState } from 'react';
import { X, Link2, Search } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal to link a standalone receipt to an existing quote's payment ledger.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - receipt: the standalone receipt object (must have no invoice_id and no quote_id)
 *  - onLinked: (updatedQuote) => void
 */
export const LinkReceiptToQuoteModal = ({ open, onClose, receipt, onLinked }) => {
  const password = sessionStorage.getItem('admin_password');
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !receipt) return;
    setError('');
    setSelectedId('');
    setSearch('');
    setLoading(true);
    fetch(`${API_URL}/api/admin/quotes`, {
      headers: { 'Authorization': `Basic ${btoa(':' + password)}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        // Exclude paid + converted quotes — they can't take another payment
        const eligible = (data || []).filter(q => {
          const status = (q.status || '').toLowerCase();
          return status !== 'paid' && !q.converted_to_invoice;
        });
        setQuotes(eligible);
      })
      .catch(err => {
        logger.error('Failed to load quotes:', err);
        setError('Failed to load quotes.');
      })
      .finally(() => setLoading(false));
  }, [open, receipt?.id]);

  // Ranked list: exact-email match on receipt.client_email first, then filtered by search
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const clientEmail = (receipt?.client_email || '').toLowerCase();
    const base = q
      ? quotes.filter(qt =>
          (qt.quote_number || '').toLowerCase().includes(q)
          || (qt.client_name || '').toLowerCase().includes(q)
          || (qt.client_email || '').toLowerCase().includes(q)
        )
      : quotes;
    return [...base].sort((a, b) => {
      const am = (a.client_email || '').toLowerCase() === clientEmail ? -1 : 0;
      const bm = (b.client_email || '').toLowerCase() === clientEmail ? -1 : 0;
      return am - bm;
    });
  }, [quotes, search, receipt?.client_email]);

  if (!open || !receipt) return null;

  const fmtMoney = (n, currency = receipt.currency || 'CI$') =>
    `${currency}${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const selectedQuote = quotes.find(q => q.id === selectedId);

  const projectedBalance = (() => {
    if (!selectedQuote) return null;
    const total = Number(selectedQuote.total) || 0;
    const paid = (selectedQuote.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const newBalance = Math.max(total - (paid + (Number(receipt.total) || 0)), 0);
    return newBalance;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedId) {
      setError('Please select a quote first.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/receipts/${receipt.id}/link-to-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`,
        },
        body: JSON.stringify({ quote_id: selectedId }),
      });
      if (response.ok) {
        const updated = await response.json();
        onLinked?.(updated);
        onClose?.();
      } else {
        const errBody = await response.json().catch(() => ({}));
        setError(errBody.detail || 'Failed to link receipt to quote.');
      }
    } catch (err) {
      logger.error('Link receipt failed:', err);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="link-receipt-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-bold text-foreground">Link receipt to quote</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5 text-foreground/60" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-border bg-blue-50 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <div><span className="text-foreground/60">Receipt:</span> <span className="font-mono font-semibold">{receipt.receipt_number}</span></div>
            <div><span className="text-foreground/60">Client:</span> <span className="font-medium">{receipt.client_name}</span></div>
            <div><span className="text-foreground/60">Amount:</span> <span className="font-semibold">{fmtMoney(receipt.total)}</span></div>
            <div><span className="text-foreground/60">Method:</span> <span className="font-medium">{receipt.payment_method || 'bank_transfer'}</span></div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 py-4 flex-1 overflow-y-auto space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">Search quotes</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Quote number, client name, or email…"
                  className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="link-modal-search"
                />
              </div>
              <p className="text-xs text-foreground/60 mt-1">
                Only quotes that are not fully paid or converted are shown. Matching client email is listed first.
              </p>
            </div>

            {loading ? (
              <div className="text-center py-8 text-foreground/60 text-sm">Loading quotes…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-8 text-foreground/60 text-sm">No eligible quotes found.</div>
            ) : (
              <div className="border border-border rounded-lg divide-y divide-border max-h-72 overflow-y-auto">
                {filtered.map(qt => {
                  const paid = (qt.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
                  const balance = Math.max((Number(qt.total) || 0) - paid, 0);
                  const isEmailMatch = (qt.client_email || '').toLowerCase() === (receipt.client_email || '').toLowerCase();
                  const selected = selectedId === qt.id;
                  return (
                    <label
                      key={qt.id}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer text-sm ${selected ? 'bg-primary/10' : 'hover:bg-gray-50'}`}
                      data-testid={`link-modal-quote-${qt.quote_number}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <input
                          type="radio"
                          name="linked-quote"
                          value={qt.id}
                          checked={selected}
                          onChange={() => setSelectedId(qt.id)}
                          className="w-4 h-4"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-semibold text-foreground">{qt.quote_number}</span>
                            {isEmailMatch && (
                              <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                same client
                              </span>
                            )}
                          </div>
                          <div className="text-foreground/70 truncate">{qt.client_name} · {qt.client_email}</div>
                        </div>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="font-semibold text-foreground">{fmtMoney(qt.total, qt.currency)}</div>
                        <div className="text-xs text-foreground/60">Balance {fmtMoney(balance, qt.currency)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {selectedQuote && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm" data-testid="link-modal-preview">
                <div className="font-semibold text-emerald-900 mb-1">Preview after linking</div>
                <div className="text-emerald-800">
                  A payment of <strong>{fmtMoney(receipt.total, selectedQuote.currency)}</strong> ({receipt.payment_method || 'bank_transfer'})
                  will be added to <strong>{selectedQuote.quote_number}</strong>. New balance:
                  {' '}<strong>{fmtMoney(projectedBalance, selectedQuote.currency)}</strong>
                  {projectedBalance === 0 && ' — quote will be marked PAID.'}
                </div>
              </div>
            )}

            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="link-modal-error">
                {error}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-border bg-gray-50 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedId}
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="link-modal-submit"
            >
              <Link2 className="w-4 h-4" />
              {submitting ? 'Linking…' : 'Link to quote'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
